import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead,
  TableRow,
  CircularProgress, 
  Alert,
  IconButton,
  Snackbar,
  Avatar,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme
} from '@mui/material';


import { KeyboardArrowUp as KeyboardArrowUpIcon, KeyboardArrowDown as KeyboardArrowDownIcon, CalendarToday as CalendarTodayIcon, ViewList as ViewListIcon, ViewModule as ViewModuleIcon } from '@mui/icons-material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Pagination from '@mui/material/Pagination';
import { useNavigate } from 'react-router-dom';
import { ref, get, update, onValue } from 'firebase/database';
import { database } from '../firebase';
import { normalizeSortOrders } from '../utils/favoritesUtils';
import { parseDateSafariCompatible } from '../utils/dateDebugUtils';

import { EventListItem } from '../types';
import TableSortLabel from '@mui/material/TableSortLabel';

import { useSchools } from '../contexts/SchoolsContext';
import { useUser } from '../contexts/UserContext';
import { useAuth } from '../hooks/useAuth';
import { usePrograms } from '../contexts/ProgramsContext';
import { useEvents } from '../contexts/EventsContext';
import { activityLogService } from '../services/activityLogService';



interface FavoriteSchool {
  name: string;
  sortOrder: number;
  imageURL?: string;
}

interface FavoriteProgram {
  id: string;
  name: string;
  schoolName: string;
  sortOrder: number;
}

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const { schools, loadFeedIconsForSchools, feedIconsLoading } = useSchools();
  const { user, loading: userLoading } = useUser();
  const { user: authUser } = useAuth();

  // Redirect guidance users to Guidance Dashboard
  React.useEffect(() => {
    if (!userLoading && user?.isGuidanceUser) {
      navigate('/guidance-dashboard', { replace: true });
    }
  }, [userLoading, user?.isGuidanceUser, navigate]);
  const { programs, calculateEligibilityForSchools, calculateEligibilityForAllPrograms, isEligibilityCalculatedForSchools, eligibilityCalculationInProgress, getProgramEligibility, globalEligibilityCache, cacheLoading, userDataStatus } = usePrograms();
  const { events: allEvents, loading: eventsContextLoading, error: eventsContextError } = useEvents();

  // Helper function to get the correct spelling based on user's country
  const getFavouritesLabel = () => {
    return user?.country === 'US' ? 'Favorites' : 'Favourites';
  };
  
  const getFavouriteLabel = () => {
    return user?.country === 'US' ? 'Favorite' : 'Favourite';
  };

  // Helper function to get the events section title based on user's favorites
  const getEventsSectionTitle = () => {
    const favoriteSchools = allUserFavorites.map(fav => fav.name);
    const programSchools = allUserPrograms.map(prog => prog.schoolName);
    
    // For guidance users, also include their high school name
    const guidanceSchool = user?.isGuidanceUser && user?.schoolName ? [user.schoolName] : [];
    
    const allUniqueSchools = Array.from(new Set([...favoriteSchools, ...programSchools, ...guidanceSchool]));
    
    if (allUniqueSchools.length > 0) {
      return `Upcoming Events for your ${getFavouriteLabel()} Schools`;
    }
    return 'Upcoming Events';
  };

  // Helper function to get eligibility status for a program (consistent with other pages)
  const getEligibilityStatus = (program: any) => {
    // Get eligibility directly from globalEligibilityCache
    const eligibility = globalEligibilityCache[program.id];
    
    if (!eligibility) {
      return null;
    }
    
    // If ifEligible is explicitly false, return false
    if (eligibility.ifEligible === false) {
      return false;
    }
    
    // If ifEligible is explicitly true, return true
    if (eligibility.ifEligible === true) {
      return true;
    }
    
    // If ifEligible is null, it means there are some issues.
    // We need to determine if these issues make it "Not Yet Eligible" or "Other".
    if (eligibility.ifEligible === null) {
      const hasMissingCourses = (eligibility.missingCourses?.length || 0) > 0;
      const hasGradeIssues = (eligibility.gradeIssues?.length || 0) > 0;
      
      const userGrade = user?.courses ? 
        (Object.values(user.courses).some((course: any) => course.grade === '12' || course.grades === 12) ? '12' :
         Object.values(user.courses).some((course: any) => course.grade === '11' || course.grades === 11) ? '11' : '10') : '10';
      
      const hasLowAverage = userGrade === '12' &&
                           eligibility.calculatedAverage !== undefined &&
                           eligibility.programAverage !== undefined &&
                           eligibility.calculatedAverage < eligibility.programAverage;

      // If there are any specific reasons for not being eligible (missing courses, grade issues, or low average for G12),
      // then it should be classified as "Not Yet Eligible" (false).
      if (hasMissingCourses || hasGradeIssues || hasLowAverage) {
        return false;
      }
    }
    
    // Default to null for other cases
    return null;
  };

  // Handle event website click
  const handleEventWebsiteClick = async (event: React.MouseEvent, eventId: string) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (eventId && authUser?.uid) {
      // Log the external event website referral activity
      try {
        await activityLogService.logExternalEventWebsiteReferral(authUser.uid, eventId);
      } catch (error) {
        console.error('Error logging external event website referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Find the event to get the website URL
      const eventData = allEvents.find(e => e.id === eventId);
      if (eventData?.website) {
        window.open(eventData.website, '_blank');
      }
    }
  };

  const [events, setEvents] = useState<EventListItem[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [eventsLazyLoading, setEventsLazyLoading] = useState(false);
  const [allUpcomingEvents, setAllUpcomingEvents] = useState<EventListItem[]>([]);
  const INITIAL_EVENTS_LIMIT = 5;
  const TOTAL_EVENTS_LIMIT = 100;
  // Sort config for events table
  const [eventsSortConfig, setEventsSortConfig] = React.useState({ key: 'date', direction: 'asc' });

  // State for user's registered events
  const [userRegisteredEvents, setUserRegisteredEvents] = useState<any[]>([]);
  const [userRegisteredEventsLoading, setUserRegisteredEventsLoading] = useState(false);
  const [showPastUserEvents, setShowPastUserEvents] = useState(false);

  // State for current user's favorite schools
  const [currentUserFavorites, setCurrentUserFavorites] = useState<FavoriteSchool[]>([]);
  const [allUserFavorites, setAllUserFavorites] = useState<FavoriteSchool[]>([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoritesPage, setFavoritesPage] = useState(1);
  const FAVORITES_PER_PAGE = 5;
  const INITIAL_FAVORITES_LIMIT = 5;
  
  // State for current user's favorite programs
  const [currentUserPrograms, setCurrentUserPrograms] = useState<FavoriteProgram[]>([]);
  const [allUserPrograms, setAllUserPrograms] = useState<FavoriteProgram[]>([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [programsPage, setProgramsPage] = useState(1);
  const PROGRAMS_PER_PAGE = 5;
  const INITIAL_PROGRAMS_LIMIT = 5;
  
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // State for My Upcoming Events view mode
  const [myEventsViewMode, setMyEventsViewMode] = useState<'card' | 'table'>(() => {
    const savedViewMode = localStorage.getItem('dashboardMyEventsViewMode');
    if (savedViewMode === 'card' || savedViewMode === 'table') {
      return savedViewMode;
    }
    // Default to card view on mobile, table view on desktop
    return isMobile ? 'card' : 'table';
  });

  // Update view mode when mobile state changes (if no saved preference)
  useEffect(() => {
    const savedViewMode = localStorage.getItem('dashboardMyEventsViewMode');
    if (!savedViewMode) {
      setMyEventsViewMode(isMobile ? 'card' : 'table');
    }
  }, [isMobile]);

  // Helper function to format date and time for display
  const formatEventDateTime = (dateStr: string, timeStr: string): string => {
    if (!dateStr) return 'N/A';
    
    try {
      // FIX: Use Safari-compatible date parsing
      const date = parseDateSafariCompatible(dateStr);
      if (isNaN(date.getTime())) return 'N/A';
      
      // Format date as "mmm dd, yyyy"
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();
      
      const formattedDate = `${month} ${day}, ${year}`;
      
      // Format time
      let formattedTime = '';
      if (timeStr) {
        // Handle time strings like "08:00 PM" or "11:00 AM"
        const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (timeMatch) {
          let hours = parseInt(timeMatch[1], 10);
          const minutes = timeMatch[2];
          const period = timeMatch[3].toLowerCase();
          
          // Convert to 12-hour format without leading zero
          if (period === 'pm' && hours !== 12) {
            hours += 12;
          } else if (period === 'am' && hours === 12) {
            hours = 0;
          }
          
          const displayHours = hours % 12 || 12;
          const ampm = hours >= 12 ? 'p' : 'a';
          
          formattedTime = `${displayHours}:${minutes}${ampm}`;
        } else {
          // Fallback for other time formats - use Safari-compatible parsing
          const timeDate = parseDateSafariCompatible(timeStr);
          const hours = timeDate.getHours();
          const minutes = timeDate.getMinutes();
          const ampm = hours >= 12 ? 'p' : 'a';
          const displayHours = hours % 12 || 12;
          formattedTime = `${displayHours}:${minutes.toString().padStart(2, '0')}${ampm}`;
        }
      }
      
      return formattedTime ? `${formattedDate} (${formattedTime})` : formattedDate;
    } catch (error) {
      return 'N/A';
    }
  };

  // Format calendar date for display
  const formatCalendarDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Add state to track when both favorites and programs are loaded
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);
  const [programsLoaded, setProgramsLoaded] = useState(false);
  const [eligibilityCalculated, setEligibilityCalculated] = useState(false);
  const [feedIconsLazyLoaded, setFeedIconsLazyLoaded] = useState(false);

  const handleEventsSort = (columnKey: string) => {
    setEventsSortConfig((prev) => {
      if (prev.key === columnKey) {
        return { key: columnKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key: columnKey, direction: 'asc' };
    });
  };

  // Load current user's favorite schools
  useEffect(() => {
    if (!authUser?.uid) return;

    // Don't proceed if schools haven't loaded yet
    if (schools.length === 0) {
      setFavoritesLoaded(false);
      return;
    }

    // Normalize sort orders when the component loads
    normalizeSortOrders(authUser.uid).catch(error => {
      console.error('Error normalizing sort orders:', error);
    });

    const userFavoritesRef = ref(database, `user/${authUser.uid}/favorites`);
    
    // Set up real-time listener for favorites
    const unsubscribe = onValue(userFavoritesRef, (snapshot) => {
      try {
        console.log('🔄 Dashboard: Real-time favorites update received');
        if (snapshot.exists()) {
          const favoritesData = snapshot.val();
          console.log('📊 Dashboard: Raw favorites data:', favoritesData);
          const favorites: FavoriteSchool[] = [];
          
          for (const schoolName in favoritesData) {
            const schoolData = favoritesData[schoolName];
            if (schoolData.FavoritedType === 'school') {
              // Check if the school still exists in the schools array
              const schoolExists = schools.some(school => school.name === schoolName);
              
              if (schoolExists) {
                const favorite = {
                  name: schoolName,
                  sortOrder: schoolData.FavoritedSortOrder || 0,
                  imageURL: schoolData.FavoritedImageURL || ''
                };
                favorites.push(favorite);
                console.log(`📋 Dashboard: Found school ${schoolName} with sortOrder ${favorite.sortOrder}`);
              } else {
                console.log(`🗑️ Dashboard: Filtering out orphaned school ${schoolName} - school no longer exists`);
              }
            }
          }
          
          // Sort by sort order
          favorites.sort((a, b) => a.sortOrder - b.sortOrder);
          console.log('📊 Dashboard: Sorted favorites:', favorites.map(f => `${f.name} (${f.sortOrder})`));
          
          // Set all favorites (no longer limiting to top 5)
          setCurrentUserFavorites(favorites);
          setAllUserFavorites(favorites);
          
          console.log(`✅ Dashboard: Loaded all ${favorites.length} favorites`);
          
          // Feed icons are now loaded upfront with schools, no need to load separately
          
          // Mark favorites as loaded
          setFavoritesLoaded(true);
        } else {
          console.log('📊 Dashboard: No favorites data found');
          setCurrentUserFavorites([]);
          setAllUserFavorites([]);
          setFavoritesLoaded(true);
        }
      } catch (error) {
        console.error('Error loading user favorites:', error);
        setErrorMessage(`Failed to load ${getFavouriteLabel().toLowerCase()} schools`);
        setShowErrorMessage(true);
      } finally {
        setFavoritesLoading(false);
      }
    }, (error) => {
      console.error('Error in favorites listener:', error);
              setErrorMessage(`Failed to load ${getFavouriteLabel().toLowerCase()} schools`);
      setShowErrorMessage(true);
      setFavoritesLoading(false);
    });

    // Cleanup function
    return () => {
      unsubscribe();
      setFavoritesLoaded(false);
      setEligibilityCalculated(false);
      setFeedIconsLazyLoaded(false);
    };
  }, [authUser?.uid, schools]);





  // Function to move a school up in rank
  const moveSchoolUp = async (schoolName: string) => {
    if (!authUser?.uid) return;

    const currentIndex = currentUserFavorites.findIndex(fav => fav.name === schoolName);
    if (currentIndex <= 0) return; // Already at top

    try {
      setFavoritesLoading(true);
      
      // Create new array with updated sort orders
      const updatedFavorites = [...currentUserFavorites];
      const currentSchool = updatedFavorites[currentIndex];
      const previousSchool = updatedFavorites[currentIndex - 1];
      
      // Swap sort orders
      const tempSortOrder = currentSchool.sortOrder;
      currentSchool.sortOrder = previousSchool.sortOrder;
      previousSchool.sortOrder = tempSortOrder;
      
      // Update Firebase
      const updates: { [key: string]: any } = {};
      updates[`user/${authUser.uid}/favorites/${currentSchool.name}/FavoritedSortOrder`] = currentSchool.sortOrder;
      updates[`user/${authUser.uid}/favorites/${previousSchool.name}/FavoritedSortOrder`] = previousSchool.sortOrder;
      
      console.log('🔥 Dashboard: Updating Firebase with sort orders:', updates);
      await update(ref(database), updates);
      console.log('✅ Dashboard: Firebase update completed');
      
      // Reorder the array to reflect the new positions
      const reorderedFavorites = [...updatedFavorites];
      [reorderedFavorites[currentIndex], reorderedFavorites[currentIndex - 1]] = 
        [reorderedFavorites[currentIndex - 1], reorderedFavorites[currentIndex]];
      
      // Update local state with reordered array
      setCurrentUserFavorites(reorderedFavorites);
      setFavoritesPage(1); // Reset to first page when order changes
      setSuccessMessage(`${schoolName} moved up in rank`);
      setShowSuccessMessage(true);

      // Log the rank upgrade and downgrade activities
      try {
        // Find the school ID for the school that was moved up
        const school = schools.find(s => s.name === schoolName);
        if (school?.id) {
          await activityLogService.logFavoriteSchoolRankUpgrade(authUser.uid, school.id);
        }
        
        // Find the school ID for the school that was moved down
        const previousSchoolData = schools.find(s => s.name === previousSchool.name);
        if (previousSchoolData?.id) {
          await activityLogService.logFavoriteSchoolRankDowngrade(authUser.uid, previousSchoolData.id);
        }
      } catch (error) {
        console.error('Error logging favorite school rank change activities:', error);
        // Don't block the flow if logging fails
      }
    } catch (error) {
      console.error('Error moving school up:', error);
      setErrorMessage('Failed to move school up');
      setShowErrorMessage(true);
    } finally {
      setFavoritesLoading(false);
    }
  };

  // Function to move a school down in rank
  const moveSchoolDown = async (schoolName: string) => {
    if (!authUser?.uid) return;

    const currentIndex = currentUserFavorites.findIndex(fav => fav.name === schoolName);
    if (currentIndex === -1 || currentIndex >= currentUserFavorites.length - 1) return; // Already at bottom

    try {
      setFavoritesLoading(true);
      
      // Create new array with updated sort orders
      const updatedFavorites = [...currentUserFavorites];
      const currentSchool = updatedFavorites[currentIndex];
      const nextSchool = updatedFavorites[currentIndex + 1];
      
      // Swap sort orders
      const tempSortOrder = currentSchool.sortOrder;
      currentSchool.sortOrder = nextSchool.sortOrder;
      nextSchool.sortOrder = tempSortOrder;
      
      // Update Firebase
      const updates: { [key: string]: any } = {};
      updates[`user/${authUser.uid}/favorites/${currentSchool.name}/FavoritedSortOrder`] = currentSchool.sortOrder;
      updates[`user/${authUser.uid}/favorites/${nextSchool.name}/FavoritedSortOrder`] = nextSchool.sortOrder;
      
      await update(ref(database), updates);
      
      // Reorder the array to reflect the new positions
      const reorderedFavorites = [...updatedFavorites];
      [reorderedFavorites[currentIndex], reorderedFavorites[currentIndex + 1]] = 
        [reorderedFavorites[currentIndex + 1], reorderedFavorites[currentIndex]];
      
      // Update local state with reordered array
      setCurrentUserFavorites(reorderedFavorites);
      setFavoritesPage(1); // Reset to first page when order changes
      setSuccessMessage(`${schoolName} moved down in rank`);
      setShowSuccessMessage(true);

      // Log the rank downgrade and upgrade activities
      try {
        // Find the school ID for the school that was moved down
        const school = schools.find(s => s.name === schoolName);
        if (school?.id) {
          await activityLogService.logFavoriteSchoolRankDowngrade(authUser.uid, school.id);
        }
        
        // Find the school ID for the school that was moved up
        const nextSchoolData = schools.find(s => s.name === nextSchool.name);
        if (nextSchoolData?.id) {
          await activityLogService.logFavoriteSchoolRankUpgrade(authUser.uid, nextSchoolData.id);
        }
      } catch (error) {
        console.error('Error logging favorite school rank change activities:', error);
        // Don't block the flow if logging fails
      }
    } catch (error) {
      console.error('Error moving school down:', error);
      setErrorMessage('Failed to move school down');
      setShowErrorMessage(true);
    } finally {
      setFavoritesLoading(false);
    }
  };

  // Load current user's favorite programs
  useEffect(() => {
    if (!authUser?.uid || programs.length === 0) return;

    const userFavoritesRef = ref(database, `user/${authUser.uid}/favorites`);
    
    // Set up real-time listener for programs
    const unsubscribe = onValue(userFavoritesRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const favoritesData = snapshot.val();
          const programsList: FavoriteProgram[] = [];
          
          for (const itemName in favoritesData) {
            const itemData = favoritesData[itemName];
            if (itemData.FavoritedType === 'program') {
              // Find program data to get the actual name and school name
              const programData = programs.find(p => p.id === itemName);
              
              // Only include programs that still exist in the programs array
              // This filters out orphaned programs that were deleted or archived
              if (programData && programData.program && programData.schoolName) {
                programsList.push({
                  id: itemName,
                  name: programData.program,
                  schoolName: programData.schoolName,
                  sortOrder: itemData.FavoritedSortOrder || 0
                });
              } else {
                console.log(`🗑️ Dashboard: Filtering out orphaned program ${itemName} - program or school no longer exists`);
              }
            }
          }
          
          // Sort by sort order
          programsList.sort((a, b) => a.sortOrder - b.sortOrder);
          
          // Set all programs (no longer limiting to top 5)
          setCurrentUserPrograms(programsList);
          setAllUserPrograms(programsList);
          
          console.log(`✅ Dashboard: Loaded all ${programsList.length} programs`);
          
          // Mark programs as loaded
          setProgramsLoaded(true);
        } else {
          setCurrentUserPrograms([]);
          setAllUserPrograms([]);
          setProgramsLoaded(true);
        }
      } catch (error) {
        console.error('Error loading user programs:', error);
        setErrorMessage(`Failed to load ${getFavouriteLabel().toLowerCase()} programs`);
        setShowErrorMessage(true);
      } finally {
        setProgramsLoading(false);
      }
    }, (error) => {
      console.error('Error in programs listener:', error);
              setErrorMessage(`Failed to load ${getFavouriteLabel().toLowerCase()} programs`);
      setShowErrorMessage(true);
      setProgramsLoading(false);
    });

    // Cleanup function
    return () => {
      unsubscribe();
      setProgramsLoaded(false);
      setEligibilityCalculated(false);
      setFeedIconsLazyLoaded(false);
    };
  }, [authUser?.uid, programs]);

  // Two-phase eligibility calculation: prioritize favorites first, then remaining schools
  useEffect(() => {
    if (favoritesLoaded && programsLoaded && !eligibilityCalculated && !eligibilityCalculationInProgress && eventsLoaded) {
      const calculatePrioritizedEligibility = async () => {
        try {
          console.log('🔄 Dashboard: Starting prioritized eligibility calculation...');
          
          // Get all school names from both favorites and programs
          const favoriteSchools = allUserFavorites.map(fav => fav.name);
          const programSchools = allUserPrograms.map(prog => prog.schoolName);
          
          // For guidance users, also include their high school name
          const guidanceSchool = user?.isGuidanceUser && user?.schoolName ? [user.schoolName] : [];
          
          // Combine and deduplicate school names
          const allUniqueSchools = Array.from(new Set([...favoriteSchools, ...programSchools, ...guidanceSchool]));
          
          console.log('📊 Dashboard: Combined school analysis:', {
            favoriteSchools: favoriteSchools.length,
            programSchools: programSchools.length,
            uniqueSchools: allUniqueSchools.length,
            schools: allUniqueSchools
          });
          
          // Check which schools need eligibility calculation
          const schoolsNeedingCalculation = allUniqueSchools.filter(schoolName => 
            !isEligibilityCalculatedForSchools([schoolName])
          );
          
          if (schoolsNeedingCalculation.length > 0) {
            // Phase 1: Calculate eligibility for schools with favorite programs (immediate display)
            // This includes both directly favorited schools AND schools with favorited programs
            const prioritySchoolsNeedingCalculation = schoolsNeedingCalculation.filter(schoolName => 
              favoriteSchools.includes(schoolName) || programSchools.includes(schoolName)
            );
            
            if (prioritySchoolsNeedingCalculation.length > 0) {
              // Use ProgramsContext as the source of truth (which links to Firebase cache)
              const hasCachedData = Object.keys(globalEligibilityCache).length > 0;
              const isCacheLoading = cacheLoading;
              const isUserDataComplete = userDataStatus === 'complete';
              
              console.log('🔍 Dashboard: ProgramsContext state check:', {
                hasCachedData,
                isUserDataComplete,
                isCacheLoading,
                userDataStatus,
                cacheSize: Object.keys(globalEligibilityCache).length
              });
              
              if (hasCachedData && isUserDataComplete && !isCacheLoading) {
                console.log(`✅ Dashboard: Using ProgramsContext data - ${Object.keys(globalEligibilityCache).length} programs cached`);
                // Use cached program-level eligibility data directly - no need to calculate by school
                console.log('🔄 Dashboard: Using cached program-level eligibility data directly');
                console.log('📊 Dashboard: Sample cached programs:', Object.keys(globalEligibilityCache).slice(0, 5));
                setEligibilityCalculated(true);
              } else {
                console.log(`⏳ Dashboard: ProgramsContext not ready - cache: ${Object.keys(globalEligibilityCache).length}, loading: ${isCacheLoading}, userData: ${userDataStatus}`);
                console.log('🔍 Dashboard: Detailed state:', {
                  hasCachedData: hasCachedData,
                  isUserDataComplete: isUserDataComplete,
                  isCacheLoading: isCacheLoading,
                  userDataStatus: userDataStatus
                });
                // Don't calculate - just show calculating status until ProgramsContext is ready
                setEligibilityCalculated(false);
              }
            } else {
              // If no priority schools need calculation, mark as calculated immediately
              setEligibilityCalculated(true);
            }
            
            // Phase 2: Calculate eligibility for remaining schools in background
            const remainingSchoolsNeedingCalculation = schoolsNeedingCalculation.filter(schoolName => 
              !prioritySchoolsNeedingCalculation.includes(schoolName)
            );
            
            if (remainingSchoolsNeedingCalculation.length > 0) {
              console.log(`🔄 Dashboard: Phase 2 - Checking cached eligibility for ${remainingSchoolsNeedingCalculation.length} remaining schools in background:`, remainingSchoolsNeedingCalculation);
              
              // Use setTimeout to run in background without blocking the UI
              setTimeout(async () => {
                try {
                  // Check if we have cached data for remaining schools - Firebase cache is the only source of truth
                  const hasCachedData = Object.keys(globalEligibilityCache).length > 0;
                  if (hasCachedData) {
                    console.log(`✅ Dashboard: Phase 2 - Using cached eligibility data for remaining schools`);
                  } else {
                    console.log(`⏳ Dashboard: Phase 2 - No cached data, waiting for cache to load`);
                    // Don't calculate - just wait for cache to load
                  }
                } catch (error) {
                  console.error('❌ Dashboard: Error in Phase 2 cache check:', error);
                }
              }, 100); // Small delay to ensure UI is responsive
            }
          } else {
            console.log('✅ Dashboard: All schools already have eligibility calculated, skipping calculation');
            console.log('🔍 Dashboard: Final state check:', {
              hasCachedData: Object.keys(globalEligibilityCache).length > 0,
              isUserDataComplete: userDataStatus === 'complete',
              isCacheLoading: cacheLoading,
              cacheSize: Object.keys(globalEligibilityCache).length,
              userDataStatus: userDataStatus
            });
            
            // If we have cached program-level data, use it directly
            if (Object.keys(globalEligibilityCache).length > 0) {
              console.log('🔄 Dashboard: Using cached program-level eligibility data for all schools');
              console.log('📊 Dashboard: Sample cached programs:', Object.keys(globalEligibilityCache).slice(0, 5));
            }
            
            setEligibilityCalculated(true);
          }
          
        } catch (error) {
          console.error('❌ Dashboard: Error in prioritized eligibility calculation:', error);
          setEligibilityCalculated(true); // Mark as calculated to prevent retries
        }
      };
      
      calculatePrioritizedEligibility();
    }
  }, [favoritesLoaded, programsLoaded, eligibilityCalculated, eligibilityCalculationInProgress, eventsLoaded, allUserFavorites, allUserPrograms, calculateEligibilityForSchools, isEligibilityCalculatedForSchools, globalEligibilityCache, cacheLoading, userDataStatus]);

  // Reset eligibility calculation state when user data changes
  useEffect(() => {
    if (user?.courses) {
      // Reset eligibility calculation state to allow recalculation
      setEligibilityCalculated(false);
    }
  }, [user?.uid, user?.courses]);

  // Reset feed icon loading state when user changes (allows refresh on new login)
  useEffect(() => {
    if (authUser?.uid) {
      setFeedIconsLazyLoaded(false);
    }
  }, [authUser?.uid]);

  // Function to move a program up in rank
  const moveProgramUp = async (programId: string) => {
    if (!authUser?.uid) return;

    const currentIndex = currentUserPrograms.findIndex(prog => prog.id === programId);
    if (currentIndex <= 0) return; // Already at top

    try {
      setProgramsLoading(true);
      
      // Create new array with updated sort orders
      const updatedPrograms = [...currentUserPrograms];
      const currentProgram = updatedPrograms[currentIndex];
      const previousProgram = updatedPrograms[currentIndex - 1];
      
      // Swap sort orders
      const tempSortOrder = currentProgram.sortOrder;
      currentProgram.sortOrder = previousProgram.sortOrder;
      previousProgram.sortOrder = tempSortOrder;
      
      // Update Firebase
      const updates: { [key: string]: any } = {};
      updates[`user/${authUser.uid}/favorites/${currentProgram.id}/FavoritedSortOrder`] = currentProgram.sortOrder;
      updates[`user/${authUser.uid}/favorites/${previousProgram.id}/FavoritedSortOrder`] = previousProgram.sortOrder;
      
      console.log('🔥 Dashboard: Updating Firebase with program sort orders:', updates);
      await update(ref(database), updates);
      console.log('✅ Dashboard: Firebase program update completed');
      
      // Reorder the array to reflect the new positions
      const reorderedPrograms = [...updatedPrograms];
      [reorderedPrograms[currentIndex], reorderedPrograms[currentIndex - 1]] = 
        [reorderedPrograms[currentIndex - 1], reorderedPrograms[currentIndex]];
      
      // Update local state with reordered array
      setCurrentUserPrograms(reorderedPrograms);
      setProgramsPage(1); // Reset to first page when order changes
      setSuccessMessage(`${currentProgram.name} moved up in rank`);
      setShowSuccessMessage(true);

      // Log the rank upgrade and downgrade activities
      try {
        await activityLogService.logFavoriteProgramRankUpgrade(authUser.uid, programId);
        await activityLogService.logFavoriteProgramRankDowngrade(authUser.uid, previousProgram.id);
      } catch (error) {
        console.error('Error logging favorite program rank change activities:', error);
        // Don't block the flow if logging fails
      }
    } catch (error) {
      console.error('Error moving program up:', error);
      setErrorMessage('Failed to move program up');
      setShowErrorMessage(true);
    } finally {
      setProgramsLoading(false);
    }
  };

  // Function to move a program down in rank
  const moveProgramDown = async (programId: string) => {
    if (!authUser?.uid) return;

    const currentIndex = currentUserPrograms.findIndex(prog => prog.id === programId);
    if (currentIndex === -1 || currentIndex >= currentUserPrograms.length - 1) return; // Already at bottom

    try {
      setProgramsLoading(true);
      
      // Create new array with updated sort orders
      const updatedPrograms = [...currentUserPrograms];
      const currentProgram = updatedPrograms[currentIndex];
      const nextProgram = updatedPrograms[currentIndex + 1];
      
      // Swap sort orders
      const tempSortOrder = currentProgram.sortOrder;
      currentProgram.sortOrder = nextProgram.sortOrder;
      nextProgram.sortOrder = tempSortOrder;
      
      // Update Firebase
      const updates: { [key: string]: any } = {};
      updates[`user/${authUser.uid}/favorites/${currentProgram.id}/FavoritedSortOrder`] = currentProgram.sortOrder;
      updates[`user/${authUser.uid}/favorites/${nextProgram.id}/FavoritedSortOrder`] = nextProgram.sortOrder;
      
      console.log('🔥 Dashboard: Updating Firebase with program sort orders (down):', updates);
      await update(ref(database), updates);
      console.log('✅ Dashboard: Firebase program update completed (down)');
      
      // Reorder the array to reflect the new positions
      const reorderedPrograms = [...updatedPrograms];
      [reorderedPrograms[currentIndex], reorderedPrograms[currentIndex + 1]] = 
        [reorderedPrograms[currentIndex + 1], reorderedPrograms[currentIndex]];
      
      // Update local state with reordered array
      setCurrentUserPrograms(reorderedPrograms);
      setProgramsPage(1); // Reset to first page when order changes
      setSuccessMessage(`${currentProgram.name} moved down in rank`);
      setShowSuccessMessage(true);

      // Log the rank downgrade and upgrade activities
      try {
        await activityLogService.logFavoriteProgramRankDowngrade(authUser.uid, programId);
        await activityLogService.logFavoriteProgramRankUpgrade(authUser.uid, nextProgram.id);
      } catch (error) {
        console.error('Error logging favorite program rank change activities:', error);
        // Don't block the flow if logging fails
      }
    } catch (error) {
      console.error('Error moving program down:', error);
      setErrorMessage('Failed to move program down');
      setShowErrorMessage(true);
    } finally {
      setProgramsLoading(false);
    }
  };

  // Function to find school ID by name
  const findSchoolIdByName = async (schoolName: string): Promise<string | null> => {
    try {
      const schoolsRef = ref(database, 'schools');
      const snapshot = await get(schoolsRef);
      if (snapshot.exists()) {
        const schoolsData = snapshot.val();
        for (const schoolId in schoolsData) {
          const schoolData = schoolsData[schoolId];
          if (schoolData['SchoolName'] === schoolName) {
            return schoolId;
          }
        }
      }
      return null;
    } catch (error) {
      console.error('Error finding school ID:', error);
      return null;
    }
  };

  // Function to handle school click
  const handleSchoolClick = async (schoolName: string) => {
    const schoolId = await findSchoolIdByName(schoolName);
    if (schoolId) {
      navigate(`/schools/${schoolId}`, { state: { from: 'dashboard' } });
    } else {
      console.error('School not found:', schoolName);
    }
  };

  // Function to handle program click
  const handleProgramClick = (programId: string) => {
    navigate(`/programs/${programId}`, { state: { from: 'dashboard' } });
  };

  // Helper function to format date for display
  const formatEventDate = (dateTimeStr: string): string => {
    if (!dateTimeStr) return 'N/A';
    try {
      // Use Safari-compatible date parsing
      const date = parseDateSafariCompatible(dateTimeStr);
      
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      console.error('🔍 Dashboard formatEventDate - Error:', error);
      return 'N/A';
    }
  };

  // Helper function to format time for display
  const formatEventTime = (dateTimeStr: string): string => {
    if (!dateTimeStr) return 'N/A';
    try {
      // FIX: Use Safari-compatible date parsing
      const date = parseDateSafariCompatible(dateTimeStr);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'p' : 'a';
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes.toString().padStart(2, '0')}${ampm}`;
    } catch (error) {
      return 'N/A';
    }
  };

  // Helper function to get school logo
  const getSchoolLogo = (hostName: string): string | undefined => {
    if (!hostName) return undefined;
    
    // Check if event is from user's high school (for guidance users)
    if (hostName === user?.schoolName && user?.userIcon) {
      return user.userIcon;
    }
    
    // Check if it's Fosterway
    if (hostName.toLowerCase().includes('fosterway')) {
      return "https://firebasestorage.googleapis.com/v0/b/fosterwayapp.appspot.com/o/schoolIconSquare%2FFosterway%20University?alt=media&token=f129a2e4-cac6-4457-99fb-b917b4af94cd";
    }
    
    // Find school in schools context
    const school = schools.find(s => s.name === hostName);
    return school?.feedIcon || school?.resultsImage || undefined;
  };

  // Handle event click to navigate to event detail
  const handleEventClick = (eventId: string) => {
    navigate(`/events/${eventId}`, { 
      state: { 
        fromDashboard: true, 
        event: allEvents.find(e => e.id === eventId)
      }
    });
  };










  // Process events from EventsContext
  useEffect(() => {
    if (!eventsContextLoading && allEvents.length > 0) {
      console.log('🔄 Dashboard: Processing events from EventsContext...');
      
      // Convert EventsContext events to EventListItem format
      if (allEvents.length > 0) {
        console.log('📊 Dashboard: Sample event from EventsContext:', allEvents[0]);
      }
      const processedEvents: EventListItem[] = allEvents.map(event => {
        let dateStr = '';
        let timeStr = '';
        
        if (event.startTime) {
          let startTimeStr = event.startTime;
          if (typeof startTimeStr === 'string') {
            startTimeStr = startTimeStr.replace(' ', 'T');
          }
          const dateObj = new Date(startTimeStr);
          if (!isNaN(dateObj.getTime())) {
            dateStr = dateObj.toLocaleDateString();
            timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          } else {
            dateStr = 'Invalid date';
            timeStr = '';
          }
        }
        
        return {
          id: event.id,
          host: event.hostName || event.host || '',
          name: event.title || event.name || '',
          date: dateStr,
          time: timeStr,
          location: event.address || event.location || '',
          description: event.description || '',
          endTime: event.endTime,
          eventType: event.eventType,
          registerRequired: event.registerRequired,
          website: event.website,
          meetingID: event.meetingID,
          meetingPassword: event.meetingPassword,
          isPostToAll: event.isPostToAll,
          hasTarget: event.hasTarget,
          eventTargetStr: event.eventTargetStr,
          activityID: event.activityID,
          startTime: event.startTime, // Preserve original startTime for proper sorting
          private: event.private
        };
      });
      
      console.log('📊 Dashboard: Filtering and sorting events...');
      
      // Get combined list of favorited schools and schools with favorited programs
      const favoriteSchools = allUserFavorites.map(fav => fav.name);
      const programSchools = allUserPrograms.map(prog => prog.schoolName);
      
      // For guidance users, also include their high school name
      const guidanceSchool = user?.isGuidanceUser && user?.schoolName ? [user.schoolName] : [];
      
      const allUniqueSchools = Array.from(new Set([...favoriteSchools, ...programSchools, ...guidanceSchool]));
      
      console.log('📊 Dashboard: Combined school analysis for events filtering:', {
        favoriteSchools: favoriteSchools.length,
        programSchools: programSchools.length,
        uniqueSchools: allUniqueSchools.length,
        schools: allUniqueSchools
      });
      
      // Filter for upcoming events (start date >= today)
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of today
      const filteredEvents = processedEvents.filter(e => {
        // Use startTime for proper date+time filtering, fallback to date if startTime not available
        const timeStr = e.startTime || e.date;
        const timeStrFormatted = typeof timeStr === 'string' ? timeStr.replace(' ', 'T') : timeStr;
        const dt = new Date(timeStrFormatted);
        const isUpcoming = !isNaN(dt.getTime()) && dt >= today;
        
        // If user has no favorites, show all upcoming events
        if (allUniqueSchools.length === 0) {
          return isUpcoming;
        }
        
        // If user has favorites, only show events from favorited schools
        const isFromFavoritedSchool = allUniqueSchools.includes(e.host);
        
        return isUpcoming && isFromFavoritedSchool;
      });
      
      // Sort by soonest date+time
      filteredEvents.sort((a, b) => {
        const aTimeStr = (a.startTime || a.date);
        const bTimeStr = (b.startTime || b.date);
        const aTimeStrFormatted = typeof aTimeStr === 'string' ? aTimeStr.replace(' ', 'T') : aTimeStr;
        const bTimeStrFormatted = typeof bTimeStr === 'string' ? bTimeStr.replace(' ', 'T') : bTimeStr;
        return new Date(aTimeStrFormatted).getTime() - new Date(bTimeStrFormatted).getTime();
      });
      
      // Limit to total events limit
      const limitedEvents = filteredEvents.slice(0, TOTAL_EVENTS_LIMIT);
      
      // Set initial events (first 5)
      const initialEvents = limitedEvents.slice(0, INITIAL_EVENTS_LIMIT);
      
      console.log(`✅ Dashboard: Loaded ${initialEvents.length} initial events (for display) from ${limitedEvents.length} total upcoming events (limited to ${TOTAL_EVENTS_LIMIT})`);
      console.log(`📊 Dashboard: Events filtering summary:`, {
        totalProcessedEvents: processedEvents.length,
        upcomingEvents: processedEvents.filter(e => {
          const timeStr = e.startTime || e.date;
          const timeStrFormatted = typeof timeStr === 'string' ? timeStr.replace(' ', 'T') : timeStr;
          const dt = new Date(timeStrFormatted);
          return !isNaN(dt.getTime()) && dt >= today;
        }).length,
        favoritedSchoolsCount: allUniqueSchools.length,
        filteredEventsCount: filteredEvents.length,
        hasFavorites: allUniqueSchools.length > 0
      });
      setEvents(initialEvents);
      setAllUpcomingEvents(limitedEvents);
      setEventsLoaded(true);
      setEventsLoading(false);
    } else if (!eventsContextLoading && allEvents.length === 0) {
      console.log('📊 Dashboard: No events found in EventsContext');
      setEvents([]);
      setAllUpcomingEvents([]);
      setEventsLoaded(true);
      setEventsLoading(false);
    } else if (eventsContextError) {
      console.error('❌ Dashboard: EventsContext error:', eventsContextError);
      setEventsError('Failed to load events.');
      setEvents([]);
      setAllUpcomingEvents([]);
      setEventsLoaded(true);
      setEventsLoading(false);
    }
  }, [allEvents, eventsContextLoading, eventsContextError, allUserFavorites, allUserPrograms]);

  // Load user's registered events
  useEffect(() => {
    const getUserRegisteredEvents = () => {
      if (!user?.profileExtra?.eventsAddedToCalendar || !allEvents.length) {
        setUserRegisteredEvents([]);
        return;
      }

      setUserRegisteredEventsLoading(true);
      
      try {
        // Get events that are added to calendar but not cancelled
        const registeredEventIds = Object.entries(user.profileExtra.eventsAddedToCalendar)
          .filter(([eventId, eventData]: [string, any]) => {
            return eventData && !eventData.timestampCancelled;
          })
          .map(([eventId, eventData]: [string, any]) => eventId);

        // Find the actual event data from the events context
        const registeredEventsData = allEvents.filter(event => 
          registeredEventIds.includes(event.id)
        );

        // Filter and sort events based on past/future toggle
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Start of today
        
        const filteredEvents = registeredEventsData.filter(event => {
          const eventDate = new Date((event as any).startTime || 0);
          const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
          
          if (showPastUserEvents) {
            // Show only past events (yesterday and older)
            return eventDateOnly < today;
          } else {
            // Show only future events (today and future)
            return eventDateOnly >= today;
          }
        });

        // Sort events
        const sortedEvents = filteredEvents.sort((a, b) => {
          const dateA = new Date((a as any).startTime || 0);
          const dateB = new Date((b as any).startTime || 0);
          
          if (showPastUserEvents) {
            // Past events: sort descending (most recent first)
            return dateB.getTime() - dateA.getTime();
          } else {
            // Future events: sort ascending (soonest first)
            return dateA.getTime() - dateB.getTime();
          }
        });

        setUserRegisteredEvents(sortedEvents);
      } catch (error) {
        console.error('Error getting user registered events:', error);
        setUserRegisteredEvents([]);
      } finally {
        setUserRegisteredEventsLoading(false);
      }
    };

    getUserRegisteredEvents();
  }, [user?.profileExtra?.eventsAddedToCalendar, allEvents, showPastUserEvents]);

  // Lazy load remaining events after dashboard is loaded
  useEffect(() => {
    if (eventsLoaded && allUpcomingEvents.length > INITIAL_EVENTS_LIMIT && events.length === INITIAL_EVENTS_LIMIT) {
      const lazyLoadRemainingEvents = async () => {
        try {
          setEventsLazyLoading(true);
          console.log('🔄 Dashboard: Lazy loading remaining events...');
          
          // Simulate a small delay to ensure dashboard is fully rendered
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Load all remaining events
          const remainingEvents = allUpcomingEvents.slice(INITIAL_EVENTS_LIMIT);
          setEvents([...events, ...remainingEvents]);
          
          console.log(`✅ Dashboard: Lazy loaded ${remainingEvents.length} additional events`);
        } catch (error) {
          console.error('❌ Dashboard: Error lazy loading events:', error);
        } finally {
          setEventsLazyLoading(false);
        }
      };
      
      lazyLoadRemainingEvents();
    }
  }, [eventsLoaded, allUpcomingEvents.length, events.length]);

  // Note: Removed lazy loading useEffects for favorites and programs - now loading all initially
  // Note: Removed lazy loading useEffect for feed icons - now loading all initially with favorites

  // Note: Removed separate eligibility calculation useEffects - now handled by combined useEffect above



  // Sort events based on sort config
  const sortedEvents = React.useMemo(() => {
    const arr = [...events];
    arr.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      switch (eventsSortConfig.key) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'host':
          aValue = a.host?.toLowerCase() || '';
          bValue = b.host?.toLowerCase() || '';
          break;
        case 'location':
          aValue = a.location?.toLowerCase() || '';
          bValue = b.location?.toLowerCase() || '';
          break;
        case 'date':
          // Use startTime for proper date+time sorting, fallback to date if startTime not available
          const aStartTime = a.startTime || a.date;
          const bStartTime = b.startTime || b.date;
          // Handle startTime format (replace space with T for ISO format)
          const aTimeStr = typeof aStartTime === 'string' ? aStartTime.replace(' ', 'T') : aStartTime;
          const bTimeStr = typeof bStartTime === 'string' ? bStartTime.replace(' ', 'T') : bStartTime;
          aValue = new Date(aTimeStr).getTime();
          bValue = new Date(bTimeStr).getTime();
          break;
        case 'time':
          aValue = a.time || '';
          bValue = b.time || '';
          break;
        default:
          aValue = '';
          bValue = '';
      }
      if (aValue < bValue) return eventsSortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return eventsSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [events, eventsSortConfig]);

  // Show only first 5 events on Dashboard
  const paginatedEvents = sortedEvents.slice(0, 5);

  // Pagination for favorite schools
  const paginatedFavorites = currentUserFavorites.slice((favoritesPage - 1) * FAVORITES_PER_PAGE, favoritesPage * FAVORITES_PER_PAGE);
  const favoritesPageCount = Math.ceil(currentUserFavorites.length / FAVORITES_PER_PAGE);

  // Pagination for favorite programs
  const paginatedPrograms = currentUserPrograms.slice((programsPage - 1) * PROGRAMS_PER_PAGE, programsPage * PROGRAMS_PER_PAGE);
  const programsPageCount = Math.ceil(currentUserPrograms.length / PROGRAMS_PER_PAGE);



  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      {/* CSS Animation for pulse effect */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.7; }
            100% { opacity: 1; }
          }
        `}
      </style>


      {/* Charts Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        flexWrap: 'wrap', 
        gap: isMobile ? 2 : 4, 
        mb: isMobile ? 2 : 4 
      }}>
        <Paper sx={{ 
          p: isMobile ? 1 : 2, 
          flex: { xs: 'none', md: '9 1 0%' }, 
          minWidth: { xs: 'auto', md: 300 },
          width: { xs: '100%', md: 'auto' }
        }}>
          <Typography variant="h6" gutterBottom>{getFavouriteLabel()} Schools</Typography>
          {!favoritesLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress />
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  Loading {getFavouriteLabel().toLowerCase()} schools...
                </Typography>
            </Box>
          ) : (
            <>
              {feedIconsLoading && currentUserFavorites.some(fav => {
                const schoolData = schools.find(school => school.name === fav.name);
                return !schoolData?.feedIcon;
              }) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1, gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Loading school icons...
                  </Typography>
                </Box>
              )}
              {eligibilityCalculationInProgress && !eligibilityCalculated && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1, gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Calculating eligibility...
                  </Typography>
                </Box>
              )}
              {currentUserFavorites.length > 0 ? (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', mb: 2, mt: 2, maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontWeight: 'bold', 
                        minWidth: isMobile ? 40 : 80, 
                        maxWidth: isMobile ? 40 : 80, 
                        width: isMobile ? 40 : 80, 
                        textAlign: 'center' 
                      }}>
                        {!isMobile && 'Rank'}
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 'bold', 
                        width: isMobile ? 48 : 48, 
                        textAlign: 'center',
                        paddingLeft: 1,
                        paddingRight: 1
                      }}></TableCell>
                      <TableCell sx={{ 
                        fontWeight: 'bold'
                      }}>School</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 'bold', 
                        minWidth: isMobile ? 60 : 100, 
                        maxWidth: isMobile ? 60 : 100, 
                        width: isMobile ? 60 : 100 
                      }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedFavorites.map((favorite, idx) => {
                      // Find school data from schools context to get the correct image
                      const schoolData = schools.find((school) => school.name === favorite.name);
                      const schoolIcon = schoolData?.feedIcon || schoolData?.resultsImage;
                      
                      // Calculate the actual rank based on the current page
                      const actualRank = (favoritesPage - 1) * FAVORITES_PER_PAGE + idx + 1;
                      
                      return (
                        <TableRow key={favorite.name}>
                          <TableCell sx={{ verticalAlign: 'top', textAlign: 'center' }}>{actualRank}</TableCell>
                          <TableCell sx={{ 
                            verticalAlign: 'top', 
                            textAlign: 'center',
                            width: isMobile ? 48 : 48,
                            paddingLeft: 1,
                            paddingRight: 1
                          }}>
                            <Avatar
                              sx={{ 
                                width: isMobile ? 24 : 32, 
                                height: isMobile ? 24 : 32, 
                                bgcolor: 'grey.200', 
                                color: 'grey.600', 
                                fontWeight: 600,
                                cursor: 'pointer',
                                flexShrink: 0,
                                '&:hover': {
                                  opacity: 0.8
                                }
                              }}
                              onClick={() => handleSchoolClick(favorite.name)}
                            >
                              {schoolIcon ? (
                                <img
                                  src={schoolIcon}
                                  alt={favorite.name}
                                  style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }}
                                />
                              ) : (
                                favorite.name ? favorite.name[0] : ''
                              )}
                            </Avatar>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top', width: 'auto' }}>
                            <Typography
                              sx={{
                                cursor: 'pointer'
                              }}
                              onClick={() => handleSchoolClick(favorite.name)}
                            >
                              {favorite.name}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top', textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: isMobile ? 0.25 : 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => moveSchoolUp(favorite.name)}
                                disabled={actualRank === 1 || favoritesLoading}
                                sx={{ p: isMobile ? 0.25 : 0.5, color: 'primary.main' }}
                              >
                                <KeyboardArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => moveSchoolDown(favorite.name)}
                                disabled={actualRank === currentUserFavorites.length || favoritesLoading}
                                sx={{ p: isMobile ? 0.25 : 0.5, color: 'primary.main' }}
                              >
                                <KeyboardArrowDownIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {currentUserFavorites.length > 5 && (
                <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'text.secondary' }}>
                  Showing {paginatedFavorites.length} of {currentUserFavorites.length} {getFavouriteLabel().toLowerCase()} schools
                </Typography>
              )}
              {favoritesPageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={favoritesPageCount}
                    page={favoritesPage}
                    onChange={(_, value) => setFavoritesPage(value)}
                    color="primary"
                    shape="rounded"
                    size="small"
                  />
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No {getFavouriteLabel().toLowerCase()} schools added yet. Visit the Schools page to add your {getFavouritesLabel().toLowerCase()}.
            </Typography>
          )}
        </>
          )}
        </Paper>
        <Paper sx={{ 
          p: isMobile ? 1 : 2, 
          flex: { xs: 'none', md: '11 1 0%' }, 
          minWidth: { xs: 'auto', md: 300 },
          width: { xs: '100%', md: 'auto' }
        }}>
          <Typography variant="h6" gutterBottom>{getFavouriteLabel()} Programs</Typography>
          {programsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {feedIconsLoading && currentUserPrograms.some(prog => {
                const schoolData = schools.find(school => school.name === prog.schoolName);
                return !schoolData?.feedIcon;
              }) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1, gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Loading school icons...
                  </Typography>
                </Box>
              )}
              {eligibilityCalculationInProgress && !eligibilityCalculated && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1, gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Calculating eligibility...
                  </Typography>
                </Box>
              )}
              {!programsLoaded ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    Loading {getFavouriteLabel().toLowerCase()} programs...
                  </Typography>
                </Box>
              ) : currentUserPrograms.length > 0 ? (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', mb: 2, mt: 2, maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontWeight: 'bold', 
                        minWidth: isMobile ? 40 : 80, 
                        maxWidth: isMobile ? 40 : 80, 
                        width: isMobile ? 40 : 80, 
                        textAlign: 'center' 
                      }}>
                        {!isMobile && 'Rank'}
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 'bold', 
                        width: isMobile ? 48 : 48, 
                        textAlign: 'center',
                        paddingLeft: 1,
                        paddingRight: 1
                      }}></TableCell>
                      <TableCell sx={{ 
                        fontWeight: 'bold'
                      }}>Program</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 100, maxWidth: 100, width: 100 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedPrograms.map((program, idx) => {
                      // Find school data from schools context to get the correct image
                      const schoolData = schools.find((school) => school.name === program.schoolName);
                      const schoolIcon = schoolData?.feedIcon || schoolData?.resultsImage;
                      
                      // Get eligibility directly from globalEligibilityCache instead of programs array
                      const eligibility = globalEligibilityCache[program.id];
                      
                      // Debug: Log eligibility data for favorite programs
                      if (eligibility) {
                        console.log(`🎨 Dashboard: Program ${program.name} eligibility:`, {
                          ifEligible: eligibility.ifEligible,
                          hasGradeIssues: eligibility.gradeIssues && eligibility.gradeIssues.length > 0,
                          hasMissingCourses: eligibility.missingCourses && eligibility.missingCourses.length > 0
                        });
                      }
                      
                      // Calculate the actual rank based on the current page
                      const actualRank = (programsPage - 1) * PROGRAMS_PER_PAGE + idx + 1;
                      
                      // Determine border color based on eligibility and user grade
                      let borderColor = 'grey.400'; // Default grey
                      
                      // Get user's grade from courses
                      const userGrade = user?.courses ? 
                        (Object.values(user.courses).some((course: any) => course.grade === '12' || course.grades === 12) ? '12' :
                         Object.values(user.courses).some((course: any) => course.grade === '11' || course.grades === 11) ? '11' : '10') : '10';
                      
                      const isGrade10 = userGrade === '10';
                      
                      if (eligibility) {
                        if (eligibility.ifEligible === true) {
                          if (isGrade10) {
                            borderColor = '#8dd439'; // Fosterway green (vertical arrow)
                          } else {
                            borderColor = 'success.main'; // Green checkmark
                          }
                        } else if (eligibility.ifEligible === false) {
                          if (isGrade10) {
                            // Check if it's a grade issue vs missing courses
                            const hasGradeIssues = eligibility.gradeIssues && eligibility.gradeIssues.length > 0;
                            const hasMissingCourses = eligibility.missingCourses && eligibility.missingCourses.length > 0;
                            
                            // Check if this program has early prerequisites (like "Six of 4M, 4U")
                            const hasEarlyPrereqs = eligibility.alertMessage?.includes("Next year's courses will be prerequisites");
                            
                            if (hasEarlyPrereqs) {
                              borderColor = '#ffc107'; // Yellow (vertical arrow for early prerequisites)
                            } else if (hasGradeIssues) {
                              borderColor = '#ffc107'; // Yellow (vertical arrow for grade issues)
                            } else if (hasMissingCourses) {
                              borderColor = 'primary.main'; // Blue (undo arrow for missing courses)
                            } else {
                              borderColor = 'primary.main'; // Blue fallback
                            }
                          } else {
                            // Check if this is a Grade 12 student with low average
                            if (userGrade === '12' && 
                                eligibility.calculatedAverage && 
                                eligibility.programAverage && 
                                eligibility.calculatedAverage < eligibility.programAverage &&
                                (!eligibility.missingCourses || eligibility.missingCourses.length === 0) &&
                                (!eligibility.gradeIssues || eligibility.gradeIssues.length === 0)) {
                              borderColor = 'primary.main'; // Blue (AltRoute) for Grade 12 low average
                            } else {
                              borderColor = 'primary.main'; // Blue (AltRoute)
                            }
                          }
                        } else {
                          // isEligible is null - check if there are missing courses or grade issues
                          const hasGradeIssues = eligibility.gradeIssues && eligibility.gradeIssues.length > 0;
                          const hasMissingCourses = eligibility.missingCourses && eligibility.missingCourses.length > 0;
                          
                          // Check if this is a Grade 12 student with low average (even when isEligible is null)
                          if (userGrade === '12' && 
                              eligibility.calculatedAverage && 
                              eligibility.programAverage && 
                              eligibility.calculatedAverage < eligibility.programAverage &&
                              (!hasMissingCourses) &&
                              (!hasGradeIssues)) {
                            borderColor = 'primary.main'; // Blue (AltRoute) for Grade 12 low average
                          } else if (hasGradeIssues || hasMissingCourses) {
                            borderColor = 'primary.main'; // Blue (AltRoute) for missing courses or grade issues
                          } else {
                            borderColor = '#8dd439'; // FosterWay green (Assignment) for no issues
                          }
                        }
                      }

                      return (
                        <TableRow key={program.id}>
                          <TableCell sx={{ 
                            verticalAlign: 'top', 
                            textAlign: 'center',
                            position: 'relative',
                            paddingLeft: isMobile ? '8px' : '12px',
                            paddingRight: isMobile ? '4px' : '16px'
                          }}>
                            <Box
                              sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: '3px',
                                backgroundColor: borderColor
                              }}
                            />
                            {actualRank}
                          </TableCell>
                          <TableCell sx={{ 
                            verticalAlign: 'top', 
                            textAlign: 'center',
                            width: isMobile ? 48 : 48,
                            paddingLeft: 1,
                            paddingRight: 1
                          }}>
                            <Avatar
                              sx={{ 
                                width: isMobile ? 24 : 32, 
                                height: isMobile ? 24 : 32, 
                                bgcolor: 'grey.200', 
                                color: 'grey.600', 
                                fontWeight: 600,
                                cursor: 'pointer',
                                flexShrink: 0,
                                '&:hover': {
                                  opacity: 0.8
                                }
                              }}
                              onClick={() => handleSchoolClick(program.schoolName)}
                            >
                              {schoolIcon ? (
                                <img
                                  src={schoolIcon}
                                  alt={program.schoolName}
                                  style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }}
                                />
                              ) : (
                                program.schoolName ? program.schoolName[0] : ''
                              )}
                            </Avatar>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top', width: 'auto' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                sx={{
                                  cursor: 'pointer',
                                  fontWeight: 500,
                                  fontSize: '0.95rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                                onClick={() => handleProgramClick(program.id)}
                              >
                                {program.name}
                              </Typography>
                              <Typography
                                sx={{
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                                color="text.secondary"
                                onClick={() => handleSchoolClick(program.schoolName)}
                              >
                                {program.schoolName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top', textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: isMobile ? 0.25 : 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => moveProgramUp(program.id)}
                                disabled={actualRank === 1 || programsLoading}
                                sx={{ p: isMobile ? 0.25 : 0.5, color: 'primary.main' }}
                              >
                                <KeyboardArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => moveProgramDown(program.id)}
                                disabled={actualRank === currentUserPrograms.length || programsLoading}
                                sx={{ p: isMobile ? 0.25 : 0.5, color: 'primary.main' }}
                              >
                                <KeyboardArrowDownIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {currentUserPrograms.length > 5 && (
                <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'text.secondary' }}>
                  Showing {paginatedPrograms.length} of {currentUserPrograms.length} {getFavouriteLabel().toLowerCase()} programs
                </Typography>
              )}
              {programsPageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={programsPageCount}
                    page={programsPage}
                    onChange={(_, value) => setProgramsPage(value)}
                    color="primary"
                    shape="rounded"
                    size="small"
                  />
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No {getFavouriteLabel().toLowerCase()} programs added yet. Visit the Programs page to add your {getFavouritesLabel().toLowerCase()}.
            </Typography>
          )}
        </>
          )}
        </Paper>
      </Box>

      {/* Events Section - My Events or Upcoming Events */}
      <Paper sx={{ mb: isMobile ? 2 : 4, p: isMobile ? 2 : 3 }}>
        {/* Check if user has future events in their calendar */}
        {(() => {
          if (!user?.profileExtra?.eventsAddedToCalendar || !allEvents.length) {
            return false;
          }
          
          // Get events that are added to calendar but not cancelled
          const registeredEventIds = Object.entries(user.profileExtra.eventsAddedToCalendar)
            .filter(([eventId, eventData]: [string, any]) => {
              return eventData && !eventData.timestampCancelled;
            })
            .map(([eventId, eventData]: [string, any]) => eventId);

          // Find the actual event data from the events context
          const registeredEventsData = allEvents.filter(event => 
            registeredEventIds.includes(event.id)
          );

          // Check if there are any future events
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Start of today
          
          const hasFutureEvents = registeredEventsData.some(event => {
            const eventDate = new Date((event as any).startTime || 0);
            const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
            return eventDateOnly >= today;
          });
          
          return hasFutureEvents;
        })() ? (
          // Show My Events section
          <>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: isMobile ? 1 : 0
              }}>
                <Typography variant="h6">My Upcoming Events</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton
                    aria-label="Table view"
                    color={myEventsViewMode === 'table' ? 'primary' : 'default'}
                    onClick={() => {
                      setMyEventsViewMode('table');
                      localStorage.setItem('dashboardMyEventsViewMode', 'table');
                    }}
                    size="small"
                  >
                    <ViewListIcon />
                  </IconButton>
                  <IconButton
                    aria-label="Card view"
                    color={myEventsViewMode === 'card' ? 'primary' : 'default'}
                    onClick={() => {
                      setMyEventsViewMode('card');
                      localStorage.setItem('dashboardMyEventsViewMode', 'card');
                    }}
                    size="small"
                  >
                    <ViewModuleIcon />
                  </IconButton>
                </Box>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}>
                <ToggleButtonGroup
                  value={showPastUserEvents ? 'past' : 'future'}
                  exclusive
                  onChange={(event, newValue) => {
                    if (newValue !== null) {
                      setShowPastUserEvents(newValue === 'past');
                    }
                  }}
                  aria-label="event filter"
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      color: 'text.secondary',
                      borderColor: 'divider',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="future" aria-label="show future events">
                    Show Future Events
                  </ToggleButton>
                  <ToggleButton value="past" aria-label="show past events">
                    Show Past Events
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
            
            {userRegisteredEventsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : userRegisteredEvents.length > 0 ? (
              <>
                {myEventsViewMode === 'card' ? (
                  // Card View
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
                    {userRegisteredEvents.slice(0, 5).map((event) => (
                      <Box 
                        key={event.id} 
                        sx={{ 
                          border: '1px solid #eee', 
                          borderRadius: 2, 
                          p: 2, 
                          bgcolor: 'background.paper', 
                          boxShadow: 1, 
                          cursor: 'pointer',
                          '&:hover': { 
                            boxShadow: 2,
                            borderColor: 'primary.main'
                          }
                        }}
                        onClick={() => handleEventClick(event.id)}
                      >
                        <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
                          {(event as any).title || (event as any).name}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          {(() => {
                            const hostName = (event as any).hostName || (event as any).host;
                            const schoolLogo = getSchoolLogo(hostName);
                            return (
                              <Avatar
                                src={schoolLogo || undefined}
                                alt={hostName}
                                sx={{ 
                                  width: 24, 
                                  height: 24, 
                                  bgcolor: 'grey.200', 
                                  color: 'grey.600', 
                                  fontWeight: 600,
                                  mr: 1
                                }}
                              >
                                {!schoolLogo && hostName ? hostName[0] : ''}
                              </Avatar>
                            );
                          })()}
                          <Typography variant="body2" color="text.secondary">
                            {(event as any).hostName || (event as any).host}
                            {(event as any).private && (
                              <LockOutlinedIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
                            )}
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          📅 {formatEventDate((event as any).startTime)} ({formatEventTime((event as any).startTime)})
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          📍 {(event as any).address || (event as any).location || 'Virtual'}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          {(() => {
                            const eventId = (event as any).id;
                            const calendarData = user?.profileExtra?.eventsAddedToCalendar?.[eventId];
                            if (calendarData && !calendarData.timestampCancelled) {
                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CalendarTodayIcon color="success" sx={{ fontSize: 16 }} />
                                  <Typography variant="caption" color="success.main" sx={{ fontSize: '0.75rem' }}>
                                    Added to Calendar
                                  </Typography>
                                </Box>
                              );
                            }
                            return null;
                          })()}
                          
                          {(event as any).website && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => handleEventWebsiteClick(e, event.id)}
                              sx={{ ml: 'auto' }}
                            >
                              Website
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  // Table View
                  <TableContainer component={Paper} sx={{ boxShadow: 'none', mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: 60 }}></TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Event Host</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Event Title</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Event Location</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Event Date</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Calendar Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userRegisteredEvents.slice(0, 5).map((event) => (
                          <TableRow 
                            key={event.id}
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'grey.50' }
                            }}
                            onClick={() => handleEventClick(event.id)}
                          >
                            <TableCell sx={{ textAlign: 'center' }}>
                              {(() => {
                                const hostName = (event as any).hostName || (event as any).host;
                                const schoolLogo = getSchoolLogo(hostName);
                                return (
                                  <Avatar
                                    src={schoolLogo || undefined}
                                    alt={hostName}
                                    sx={{ 
                                      width: 24, 
                                      height: 24, 
                                      bgcolor: 'grey.200', 
                                      color: 'grey.600', 
                                      fontWeight: 600 
                                    }}
                                  >
                                    {!schoolLogo && hostName ? hostName[0] : ''}
                                  </Avatar>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {(event as any).hostName || (event as any).host}
                                {(event as any).private && (
                                  <LockOutlinedIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {(event as any).title || (event as any).name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {(event as any).address || (event as any).location || 'Virtual'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatEventDate((event as any).startTime)} ({formatEventTime((event as any).startTime)})
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              {(() => {
                                const eventId = (event as any).id;
                                const calendarData = user?.profileExtra?.eventsAddedToCalendar?.[eventId];
                                if (calendarData && !calendarData.timestampCancelled) {
                                  return (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <CalendarTodayIcon color="success" sx={{ fontSize: 16 }} />
                                        <Typography variant="caption" color="success.main" sx={{ fontSize: '0.75rem' }}>
                                          Added to Calendar on {formatCalendarDate(calendarData.timestampAdded)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  );
                                }
                                return (
                                  <Typography variant="body2" color="text.secondary">
                                    —
                                  </Typography>
                                );
                              })()}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              {(event as any).website ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={(e) => handleEventWebsiteClick(e, event.id)}
                                >
                                  WEBSITE
                                </Button>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No {showPastUserEvents ? 'past' : 'future'} events in your calendar
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Events you add to your calendar from the Events page will appear here
                </Typography>
                <Button 
                  variant="outlined" 
                  sx={{ mt: 2 }}
                  onClick={() => navigate('/events')}
                >
                  Browse Events
                </Button>
              </Box>
            )}
          </>
        ) : (
          // Show Upcoming Events section (fallback)
          <>
            <Box sx={{ 
              mb: 2,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 1, md: 0 }
            }}>
              <Typography variant="h6" sx={{ mb: { xs: 1, md: 0 } }}>{getEventsSectionTitle()}</Typography>
              {(() => {
                // Check if user has any past events in their calendar to show toggle
                if (!user?.profileExtra?.eventsAddedToCalendar || !allEvents.length) {
                  return null;
                }
                
                const registeredEventIds = Object.entries(user.profileExtra.eventsAddedToCalendar)
                  .filter(([eventId, eventData]: [string, any]) => {
                    return eventData && !eventData.timestampCancelled;
                  })
                  .map(([eventId, eventData]: [string, any]) => eventId);

                const registeredEventsData = allEvents.filter(event => 
                  registeredEventIds.includes(event.id)
                );

                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                
                const hasPastEvents = registeredEventsData.some(event => {
                  const eventDate = new Date((event as any).startTime || 0);
                  const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
                  return eventDateOnly < today;
                });
                
                if (!hasPastEvents) {
                  return null;
                }
                
                return (
                  <ToggleButtonGroup
                    value={showPastUserEvents ? 'past' : 'future'}
                    exclusive
                    onChange={(event, newValue) => {
                      if (newValue !== null) {
                        setShowPastUserEvents(newValue === 'past');
                      }
                    }}
                    aria-label="event filter"
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        color: 'text.secondary',
                        borderColor: 'divider',
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="future" aria-label="show future events">
                      Show Future Events
                    </ToggleButton>
                    <ToggleButton value="past" aria-label="show past events">
                      Show Past Events
                    </ToggleButton>
                  </ToggleButtonGroup>
                );
              })()}
            </Box>
            {(() => {
              // If showing past events, display user's past events from calendar
              if (showPastUserEvents) {
                const pastEvents = userRegisteredEvents.filter(event => {
                  const eventDate = new Date((event as any).startTime || 0);
                  const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
                  const now = new Date();
                  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  return eventDateOnly < today;
                });

                if (pastEvents.length === 0) {
                  return (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary" gutterBottom>
                        No past events in your calendar
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Events you add to your calendar from the Events page will appear here
                      </Typography>
                      <Button 
                        variant="outlined" 
                        sx={{ mt: 2 }}
                        onClick={() => navigate('/events')}
                      >
                        Browse Events
                      </Button>
                    </Box>
                  );
                }

                return (
                  <TableContainer component={Paper} sx={{ boxShadow: 'none', mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: 60 }}></TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Event Host</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Event Title</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Event Location</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Event Date</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Calendar Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pastEvents.slice(0, 5).map((event) => (
                          <TableRow 
                            key={event.id}
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'grey.50' }
                            }}
                            onClick={() => handleEventClick(event.id)}
                          >
                            <TableCell sx={{ textAlign: 'center' }}>
                              {(() => {
                                const hostName = (event as any).hostName || (event as any).host;
                                const schoolLogo = getSchoolLogo(hostName);
                                return (
                                  <Avatar
                                    src={schoolLogo || undefined}
                                    alt={hostName}
                                    sx={{ 
                                      width: 24, 
                                      height: 24, 
                                      bgcolor: 'grey.200', 
                                      color: 'grey.600', 
                                      fontWeight: 600 
                                    }}
                                  >
                                    {!schoolLogo && hostName ? hostName[0] : ''}
                                  </Avatar>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {(event as any).hostName || (event as any).host}
                                {(event as any).private && (
                                  <LockOutlinedIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {(event as any).title || (event as any).name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {(event as any).address || (event as any).location || 'Virtual'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatEventDate((event as any).startTime)} ({formatEventTime((event as any).startTime)})
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              {(() => {
                                const eventId = (event as any).id;
                                const calendarData = user?.profileExtra?.eventsAddedToCalendar?.[eventId];
                                if (calendarData && !calendarData.timestampCancelled) {
                                  return (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <CalendarTodayIcon color="success" sx={{ fontSize: 16 }} />
                                        <Typography variant="caption" color="success.main" sx={{ fontSize: '0.75rem' }}>
                                          Added to Calendar on {formatCalendarDate(calendarData.timestampAdded)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  );
                                }
                                return (
                                  <Typography variant="body2" color="text.secondary">
                                    —
                                  </Typography>
                                );
                              })()}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              {(event as any).website ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={(e) => handleEventWebsiteClick(e, event.id)}
                                >
                                  WEBSITE
                                </Button>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              }

              // Show upcoming events (not necessarily in calendar)
              return (
                <>
                  {eventsLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 120, gap: 2 }}>
                      <CircularProgress />
                      <Typography variant="body2" color="text.secondary">
                        {eventsLoaded ? 'Processing events...' : 'Loading events from database...'}
                      </Typography>
                    </Box>
                  ) : eventsError ? (
                    <Alert severity="error">{eventsError}</Alert>
                  ) : (
                    <>
                      {eventsLazyLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2, gap: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">
                            Loading more events...
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ width: '100%', overflowX: 'auto' }}>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                          <Box component="thead">
                            <Box component="tr" sx={{ fontSize: '0.85rem', fontWeight: 'bold', fontFamily: 'Roboto, Arial, sans-serif' }}>
                              <Box component="th" sx={{ p: 1, textAlign: 'left', verticalAlign: 'bottom', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold', fontFamily: 'Roboto, Arial, sans-serif' }} onClick={() => handleEventsSort('date')}>
                                <TableSortLabel
                                  active={eventsSortConfig.key === 'date'}
                                  direction={eventsSortConfig.key === 'date' ? (eventsSortConfig.direction as 'asc' | 'desc') : 'asc'}
                                >
                                  Date
                                </TableSortLabel>
                              </Box>
                              <Box component="th" sx={{ p: 1, textAlign: 'left', verticalAlign: 'bottom', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold', fontFamily: 'Roboto, Arial, sans-serif' }} onClick={() => handleEventsSort('host')}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                  <Box sx={{ width: 32, height: 32, mr: 2 }}></Box>
                                  <TableSortLabel
                                    active={eventsSortConfig.key === 'host'}
                                    direction={eventsSortConfig.key === 'host' ? (eventsSortConfig.direction as 'asc' | 'desc') : 'asc'}
                                  >
                                    Event Host
                                  </TableSortLabel>
                                </Box>
                              </Box>
                              <Box component="th" sx={{ p: 1, textAlign: 'left', verticalAlign: 'bottom', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold', fontFamily: 'Roboto, Arial, sans-serif' }} onClick={() => handleEventsSort('name')}>
                                <TableSortLabel
                                  active={eventsSortConfig.key === 'name'}
                                  direction={eventsSortConfig.key === 'name' ? (eventsSortConfig.direction as 'asc' | 'desc') : 'asc'}
                                >
                                  Event Name
                                </TableSortLabel>
                              </Box>
                              <Box component="th" sx={{ p: 1, textAlign: 'left', verticalAlign: 'bottom', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold', fontFamily: 'Roboto, Arial, sans-serif' }} onClick={() => handleEventsSort('location')}>
                                <TableSortLabel
                                  active={eventsSortConfig.key === 'location'}
                                  direction={eventsSortConfig.key === 'location' ? (eventsSortConfig.direction as 'asc' | 'desc') : 'asc'}
                                >
                                  Location
                                </TableSortLabel>
                              </Box>
                            </Box>
                          </Box>
                          <Box component="tbody">
                            {(eventsContextLoading || eventsLoading) && !eventsLoaded ? (
                              // Show loading skeleton rows
                              Array.from({ length: 5 }).map((_, index) => (
                                <Box
                                  component="tr"
                                  key={`loading-${index}`}
                                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                                >
                                  <Box component="td" sx={{ p: 1, textAlign: 'center' }}>
                                    <Box sx={{ width: 100, height: 16, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
                                  </Box>
                                  <Box component="td" sx={{ p: 1, textAlign: 'left' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'grey.200', mr: 2 }} />
                                      <Box sx={{ width: 120, height: 16, bgcolor: 'grey.200', borderRadius: 1 }} />
                                    </Box>
                                  </Box>
                                  <Box component="td" sx={{ p: 1, textAlign: 'left' }}>
                                    <Box sx={{ width: 200, height: 16, bgcolor: 'grey.200', borderRadius: 1 }} />
                                  </Box>
                                  <Box component="td" sx={{ p: 1, textAlign: 'left' }}>
                                    <Box sx={{ width: 150, height: 16, bgcolor: 'grey.200', borderRadius: 1 }} />
                                  </Box>
                                </Box>
                              ))
                            ) : eventsContextError ? (
                              <Box component="tr">
                                <Box component="td" colSpan={4} align="center" sx={{ color: 'error.main' }}>
                                  Error loading events: {eventsContextError}
                                </Box>
                              </Box>
                            ) : paginatedEvents.length > 0 ? paginatedEvents.map(event => (
                              <Box
                                component="tr"
                                key={event.id}
                                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'grey.50' }, borderBottom: 1, borderColor: 'divider', fontSize: '0.85rem', fontFamily: 'Roboto, Arial, sans-serif' }}
                                onClick={() => navigate(`/events/${event.id}`, { state: { event } })}
                              >
                                <Box component="td" sx={{ p: 1, textAlign: 'left', fontSize: '0.85rem', fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 400 }}>
                                  {formatEventDateTime(event.date, event.time || '')}
                                </Box>
                                <Box component="td" sx={{ p: 1, textAlign: 'left', fontSize: '0.85rem', fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 400, display: 'flex', alignItems: 'center' }}>
                                  {(() => {
                                    if (event.host === user?.schoolName && user?.userIcon) {
                                      return (
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.200', color: 'grey.600', fontWeight: 600, mr: 2 }}>
                                          <img src={user.userIcon} alt={user.schoolName} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }} />
                                        </Avatar>
                                      );
                                    }
                                    const school = schools.find(s => s.name === event.host);
                                    if (school && (school.feedIcon || school.resultsImage)) {
                                      return (
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.200', color: 'grey.600', fontWeight: 600, mr: 2 }}>
                                          <img src={school.feedIcon || school.resultsImage} alt={school.name} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }} />
                                        </Avatar>
                                      );
                                    }
                                    // Fallback for Fosterway events
                                    if (event.host.toLowerCase().includes('fosterway')) {
                                      return (
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.200', color: 'grey.600', fontWeight: 600, mr: 2 }}>
                                          <img src="https://firebasestorage.googleapis.com/v0/b/fosterwayapp.appspot.com/o/schoolIconSquare%2FFosterway%20University?alt=media&token=f129a2e4-cac6-4457-99fb-b917b4af94cd" alt="Fosterway University" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }} />
                                        </Avatar>
                                      );
                                    }
                                    return null;
                                  })()}
                                  {event.host}
                                </Box>
                                <Box component="td" sx={{ p: 1, textAlign: 'left', fontSize: '0.85rem', fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 400 }}>{event.name}</Box>
                                <Box component="td" sx={{ p: 1, textAlign: 'left', fontSize: '0.85rem', fontFamily: 'Roboto, Arial, sans-serif', fontWeight: 400 }}>{event.location}</Box>
                              </Box>
                            )) : (
                              <Box component="tr">
                                <Box component="td" colSpan={4} align="center">No upcoming events found.</Box>
                              </Box>
                            )}
                          </Box>
                        </Box>
                        {events.length > 5 && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, width: '100%' }}>
                            <Typography
                              role="button"
                              onClick={() => navigate('/events')}
                              sx={{ 
                                cursor: 'pointer',
                                color: 'primary.main',
                                fontSize: '0.875rem',
                                textTransform: 'none',
                                '&:hover': {
                                  textDecoration: 'none'
                                }
                              }}
                            >
                              Show More Events
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </>
                  )}
                </>
              );
            })()}
          </>
        )}
      </Paper>

      {/* Success Message */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setShowSuccessMessage(false)}
        message={successMessage}
      />

      {/* Error Message */}
      <Snackbar
        open={showErrorMessage}
        autoHideDuration={6000}
        onClose={() => setShowErrorMessage(false)}
        message={errorMessage}
      />
    </Box>
  );
};

export default Dashboard; 